/**
 * Story registry for the Figma playground.
 *
 * Discovered from the filesystem instead of a hand-maintained list: a new
 * `<Component>.figma-story.vue` appears in the playground nav as soon as the file
 * exists. Loaders stay lazy, so a component page only pulls in its own story chunk.
 *
 * This file deliberately sits outside `pages/`, `components/` and `utils/`. Nuxt scans
 * `pages/` using `extensions` (which includes `.ts`, so a helper there would become a
 * route), it scans `components/` for auto-imports, and anything under `utils/` is
 * auto-imported into every app that extends this layer — while the playground is
 * stripped from production builds (see `ignore` in nuxt.config.ts).
 */
import { defineAsyncComponent, type Component } from 'vue';

const STORY_SUFFIX = '.figma-story.vue';

const storyFiles = import.meta.glob<{ default: Component }>('../components/figma/*/*.figma-story.vue');

// Code Connect mappings, read as text rather than imported: they pull in
// @figma/code-connect, which is a build-time dependency with no business in the browser
// bundle. Eager because they are a few lines each and the nav renders a badge for all of
// them at once.
const mappingSources = import.meta.glob<string>('../components/figma/*/*.figma.ts', {
  query: '?raw',
  import: 'default',
  eager: true,
});

/**
 * - `connected`  — an active `figma.connect(...)` mapping.
 * - `disabled`   — the file exists but the mapping is commented out (e.g. FigmaIcon while
 *                  its icon enum is in flux). Still a valid checked-in state.
 * - `missing`    — no `<Component>.figma.ts` at all.
 *
 * This is the static half of what `pnpm figma:verify` checks. Whether a mapping still
 * resolves against the live file is only answered by `figma connect publish --dry-run`,
 * which needs a token and a network call, so the badge never claims it.
 */
export type CodeConnectStatus = 'connected' | 'disabled' | 'missing';

export type PlaygroundStory = {
  /** Route segment, e.g. `button-arrow`. */
  slug: string;
  /** Component name, e.g. `FigmaButtonArrow`. */
  name: string;
  codeConnect: CodeConnectStatus;
  /** Figma node the mapping points at — present for `disabled` mappings too. */
  figmaUrl: string | undefined;
  load: () => Promise<{ default: Component }>;
};

/**
 * The reading order the single-page playground used: primitives first, then the
 * compositions built from them. Alphabetical would put FigmaBaseContainer at the top and
 * scatter the button family, so the curation is worth keeping by hand. Anything not
 * listed sorts alphabetically after these; a stale entry is harmless.
 */
const ORDER = [
  'icon',
  'usp-icon',
  'label',
  'discount-label',
  'radiobutton',
  'radio',
  'button',
  'button-arrow',
  'button-swipe',
  'button-row',
  'cover',
  'product-price',
  'product-item-actions',
  'product-item',
  'product-item-selectable',
  'product-banner',
  'hero-addons',
  'category-collection-intro',
  'product-item-collection',
  'promo-product',
  'product-hero-banner',
  'category-hero',
  'campaign-hero',
  'intro-navigation',
  'seo-section',
  'kiosk-seo-geo',
  'newsletter',
  'divider',
  'page-link',
  'nav-link',
  'price-hero',
  'usp-list',
  'usp-highlight',
  'product-card',
  'service-block',
  'base-container',
  'base-container-background',
  'top-banner-item',
  'top-banner',
  'kiosk-header-search-bar',
  'kiosk-header',
  'breadcrumb',
  'nav-panel',
  'nav-menu',
  'nav-menu-item',
  'nav-sub-menu',
  'nav-sub-menu-item',
] as const;

/**
 * `FigmaButtonArrow` → `button-arrow`. These are the same ids the single-page playground
 * used as scroll anchors, so links that were shared before the split still point at the
 * component they named.
 */
function toSlug(name: string) {
  return name
    .slice('Figma'.length)
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase();
}

// Same comment stripping as scripts/verify-figma-mappings.ts: a mapping parked behind
// `//` or `/* */` must not read as connected.
function stripComments(source: string) {
  return source.replace(/\/\*[\s\S]*?\*\//g, '').replace(/^\s*\/\/.*$/gm, '');
}

function readMapping(name: string) {
  const source = mappingSources[`../components/figma/${name}/${name}.figma.ts`];

  if (source === undefined) return { codeConnect: 'missing' as const, figmaUrl: undefined };

  const active = stripComments(source);
  const codeConnect = /figma\.connect\s*\(/.test(active) ? ('connected' as const) : ('disabled' as const);
  // Fall back to the raw source so a commented-out mapping still links to its node.
  const figmaUrl =
    active.match(/figma\.connect\(\s*['"`]([^'"`]+)['"`]/)?.[1] ??
    source.match(/https:\/\/www\.figma\.com\/design\/[^'"`\s]+/)?.[0];

  return { codeConnect, figmaUrl };
}

function orderIndex(slug: string) {
  const index = ORDER.indexOf(slug as (typeof ORDER)[number]);
  return index === -1 ? ORDER.length : index;
}

export const stories: PlaygroundStory[] = Object.entries(storyFiles)
  .map(([path, load]) => {
    const name = path.slice(path.lastIndexOf('/') + 1, -STORY_SUFFIX.length);
    return { slug: toSlug(name), name, load, ...readMapping(name) };
  })
  .sort((a, b) => orderIndex(a.slug) - orderIndex(b.slug) || a.name.localeCompare(b.name));

export function findStory(slug: string | undefined) {
  return stories.find((story) => story.slug === slug);
}

const resolvedComponents = new Map<string, Component>();

/**
 * Memoised: navigating away and back would otherwise build a fresh async wrapper each
 * time and re-suspend on a chunk the browser already has.
 */
export function resolveStoryComponent(story: PlaygroundStory) {
  const cached = resolvedComponents.get(story.slug);
  if (cached) return cached;

  const component = defineAsyncComponent(story.load);
  resolvedComponents.set(story.slug, component);

  return component;
}
